<template>
  <div class="p-4">
    <h4>Create New Template</h4>

    <form @submit="onSubmit">
      <!-- Name -->
      <div class="mb-4">
        <label for="name">
          Name
        </label>

        <Input
          v-model.trim="form.name"
          name="name"
          type="text"
          :disabled="saving"
          required
        />
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label for="description" class="mb-0">
          Description
        </label>

        <RichInput
          :value="form.description"
          name="description"
          :disabled="saving"
          :multi-line="true"
          :show-link="false"
          required
          @input="onRichInputUpdate"
        />
      </div>

      <!-- Type -->
      <div class="mb-4">
        <label for="types">
          Types
        </label>

        <Selector
          name="types-selector"
          :options="templateTypes"
          :disabled="saving"
          :loading="$apollo.queries.templateTypes.loading"
          :clearable="false"
          placeholder="Choose a type..."
          required
          :reduce="(type) => type.templateTypeId"
          @input="form.type = $event"
        />
      </div>

      <!-- Category -->
      <div class="mb-4">
        <label for="category-selector">
          Category
        </label>

        <Selector
          name="category-selector"
          :options="categories"
          :disabled="saving"
          :loading="$apollo.queries.categories.loading"
          :clearable="false"
          placeholder="Choose a category..."
          required
          :reduce="(category) => category.categoryId"
          @input="form.categoryId = $event"
        />
      </div>

      <!-- Tags -->
      <div class="mb-4">
        <label for="tags-selector">
          Tags
        </label>

        <Selector
          name="tags-selector"
          :clearable="true"
          :disabled="saving"
          :filterable="false"
          :multiple="true"
          :no-drop="true"
          :push-tags="true"
          :searchable="false"
          :taggable="true"
          placeholder="Enter tag & hit return..."
          class="tag-selector"
          required
          @input="form.tags = saveTag($event)"
        />
      </div>

      <!-- Price -->
      <div class="mb-4">
        <label for="price">
          Price
        </label>

        <div class="input-group w-full">
          <div class="input-group-prepend">
            <span class="input-group-text">
              $
            </span>
          </div>

          <Input
            v-model.number="form.price"
            name="price"
            type="number"
            :disabled="saving"
            required
            class="w-1/4"
            input-class="rounded-l-none"
            step=".01"
          />
        </div>
      </div>

      <!-- Submit -->
      <div class="mt-8">
        <Button class="secondary" :loading="saving" type="submit">
          {{ saveLabel }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import lowerCase from "lodash.lowercase";

import Input from "@/components/UI/Input";

import Selector from "@/components/UI/Selector";
import RichInput from "@/components/UI/RichInput";
import { Categories, TemplateTypes } from "@/graphql/queries";
import { CreateBaseTemplate } from "@/graphql/mutations";
import { isAuthError } from "@/lib/auth";

export default {
  apollo: {
    categories: {
      query: () => Categories,
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          this.errors = graphQLErrors.map(({ message }) => message);
          this.$modal.show("error", {
            errors: this.errors,
            errorAction: () => {
              this.$modal.hide("error");
              this.$apollo.queries.categories.refetch();
            },
            errorActionLabel: "Reload categories?",
          });
        }
      },
    },
    templateTypes: {
      query: () => TemplateTypes,
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          this.errors = graphQLErrors.map(({ message }) => message);
          this.$modal.show("error", {
            errors: this.errors,
            errorAction: () => {
              this.$modal.hide("error");
              this.$apollo.queries.types.refetch();
            },
            errorActionLabel: "Reload types?",
          });
        }
      },
    },
  },
  name: "Template",
  components: { Input, RichInput, Selector },
  props: {
    campaignId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      categories: [],
      templateTypes: [],
      errors: [],
      form: {
        campaignId: this.campaignId,
        name: "",
        categoryId: "",
        description: "",
        price: "",
        tags: [],
        type: "",
      },
      saving: false,
    };
  },
  computed: {
    saveLabel() {
      if (this.saving) return "Saving...";

      return "Create Template";
    },
  },
  methods: {
    async onSubmit(e) {
      e.preventDefault();
      this.saving = true;
      this.errors = [];

      // Create a copy of the form data...
      const input = JSON.parse(JSON.stringify(this.form));

      // Convert tags to objects
      input.tags = input.tags.map((tag) => ({ value: tag }));

      try {
        // Wait for the mutation to complete...
        const result = await this.$apollo.mutate({
          mutation: CreateBaseTemplate,
          variables: { input },
        });

        // Get the baseTemplate and category IDs...
        const baseTemplateId = this.$get(
          result,
          "data.createBaseTemplate.baseTemplateId"
        );
        const categoryId = input.categoryId;

        // Show an alert to let the user know it's complete...
        this.$modal.show("dialog", {
          title: "Success!",
          text: "Your template has been created.",
          buttons: [
            {
              title: "View",
              class: "vue-dialog-button bg-success hover:bg-primary-alt",
              handler: () => {
                this.$modal.hide("dialog");
                this.$router.push(`/templates/${categoryId}/${baseTemplateId}`);
              },
            },
            {
              title: "Close",
            },
          ],
        });

        this.saving = false;
      } catch (error) {
        this.errors.push(error);
        this.$modal.show("error", {
          errors: this.errors,
        });
        this.saving = false;
      }
    },
    onRichInputUpdate(html) {
      this.form.description = html;
    },
    saveTag(tags) {
      // Lowercase tags on save...
      return tags.map((tag) => lowerCase(tag));
    },
  },
};
</script>

<style scoped>
/* purgecss start ignore */
/* purgecss end ignore */
</style>
